import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import {
    FixedTimesField,
    IntervalFields,
    WeekdayPicker,
} from './irrigation-schedule-fields'
import { IrrigationSchedulePreview } from './irrigation-schedule-preview'
import type {
    IrrigationDevice,
    IrrigationScheduleConfig,
} from '@/lib/api/irrigation-devices-api'
import type { IrrigationScheduleFormValues } from '@/lib/schemas/irrigation-schemas'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useDebounced } from '@/hooks/use-debounced'
import { useSaveIrrigationSchedule } from '@/hooks/use-irrigation-schedule-query'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { fieldOfApiError, scheduleSummary } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'
import {
    createIrrigationScheduleSchema,
    toScheduleConfig,
    toScheduleFormValues,
} from '@/lib/schemas/irrigation-schemas'

const FORM_FIELDS = [
    'mode',
    'weekdays',
    'duration_minutes',
    'start_times',
    'window_start',
    'window_end',
    'every_minutes',
] as const

export function modeLabel(mode: 'fixed' | 'interval'): string {
    return mode === 'fixed'
        ? m.irrigation_schedule_mode_fixed()
        : m.irrigation_schedule_mode_interval()
}

/** Dữ liệu server (đã tuần tự hóa) mà form dựng lên từ đó. */
export interface DraftBasis {
    config: string
    status: string
}

interface Props {
    device: IrrigationDevice
    mode: 'fixed' | 'interval'
    /** Cấu hình đã lưu của loại lịch này (null = chưa lưu lần nào). */
    saved: IrrigationScheduleConfig | null | undefined
    /** Loại lịch này đang được áp dụng. */
    isActive: boolean
    /** Loại lịch kia đang được áp dụng. */
    otherActive: boolean
    canEdit: boolean
    /** Dữ liệu server mà form dựng lên từ đó, gửi kèm phần sửa dở. */
    basis: DraftBasis
    /**
     * Phần đang sửa dở của loại này (giữ khi chuyển tab), nếu có.
     * keepActive=false: trạng thái áp dụng đã đổi, công tắc nạp lại mặc định.
     */
    draft?: { values: IrrigationScheduleFormValues; keepActive: boolean }
    /** Nhận lại phần đang sửa khi form bị gỡ (chuyển tab hoặc lưu xong). */
    onDraft: (
        mode: 'fixed' | 'interval',
        draft: { basis: DraftBasis; values: IrrigationScheduleFormValues },
    ) => void
}

/**
 * Cấu hình MỘT loại lịch. Cấu hình luôn được lưu; việc có áp dụng hay không
 * chỉ là công tắc "Áp dụng lịch này", nên đổi qua lại giữa các loại không
 * phải nhập lại. Quy tắc lịch do backend kiểm; lỗi trả về gắn vào đúng ô.
 */
export function IrrigationScheduleModeForm({
    device,
    mode,
    saved,
    isActive,
    otherActive,
    canEdit,
    basis,
    draft,
    onDraft,
}: Props) {
    const save = useSaveIrrigationSchedule(device.id, device.name)
    // Mở loại lịch chưa áp dụng thì mặc định bật công tắc: phần lớn người mở
    // tab là để chuyển sang dùng lịch đó.
    const defaultActive = isActive || (!saved && !otherActive)
    const form = useForm<IrrigationScheduleFormValues>({
        resolver: zodResolver(createIrrigationScheduleSchema()),
        defaultValues: draft
            ? {
                  ...draft.values,
                  active: draft.keepActive
                      ? draft.values.active
                      : defaultActive,
              }
            : toScheduleFormValues(mode, saved, defaultActive),
    })

    // Chuyển tab thì form này bị gỡ: trả phần đang sửa cho form cha giữ lại.
    // Phụ thuộc chuỗi, không phụ thuộc object basis (mỗi lần render một object
    // mới), để cleanup chỉ chạy khi gỡ form.
    const { config: basisConfig, status: basisStatus } = basis
    useEffect(
        () => () =>
            onDraft(mode, {
                basis: { config: basisConfig, status: basisStatus },
                values: form.getValues(),
            }),
        [basisConfig, basisStatus, form, mode, onDraft],
    )

    const values = useWatch({
        control: form.control,
    }) as IrrigationScheduleFormValues
    const config = toScheduleConfig(values)
    // Xem trước gọi backend: chờ người dùng ngừng gõ rồi mới gọi.
    const debounced = useDebounced(config ? JSON.stringify(config) : null, 400)
    const other = modeLabel(mode === 'fixed' ? 'interval' : 'fixed')

    const submit = form.handleSubmit(async (v) => {
        const cfg = toScheduleConfig(v)
        if (!cfg) return
        try {
            await save.mutateAsync({ mode, config: cfg, active: v.active })
        } catch (err) {
            const field = fieldOfApiError(err)
            const known = FORM_FIELDS.find((f) => f === field)
            if (err instanceof ApiError && known) {
                form.setError(known, {
                    message: resolveApiErrorMessage(err.code) ?? undefined,
                })
            }
        }
    })

    const disabled = !canEdit || save.isPending

    return (
        <FormProvider {...form}>
            <form
                noValidate
                onSubmit={(e) => void submit(e)}
                className="space-y-4"
            >
                <WeekdayPicker disabled={disabled} />
                {mode === 'fixed' ? (
                    <FixedTimesField disabled={disabled} />
                ) : (
                    <IntervalFields disabled={disabled} />
                )}
                <Field
                    name="duration_minutes"
                    label={m.irrigation_schedule_duration()}
                    required
                >
                    <Input
                        id="duration_minutes"
                        inputMode="numeric"
                        // block: <label> và <input> đều inline, w-32 để hai
                        // cái đứng chung dòng, lệch với các ô khác (nhãn ở trên).
                        className="block w-32"
                        disabled={disabled}
                        {...form.register('duration_minutes')}
                    />
                </Field>
                {config && (
                    <p className="rounded-md bg-muted px-3 py-2 text-sm font-medium">
                        {scheduleSummary(config)}
                    </p>
                )}
                <IrrigationSchedulePreview configJson={debounced} />

                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                    <div className="space-y-0.5">
                        <div className="text-sm font-medium">
                            {m.irrigation_schedule_active()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {m.irrigation_schedule_active_hint()}
                        </div>
                    </div>
                    <Controller
                        control={form.control}
                        name="active"
                        render={({ field }) => (
                            <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={disabled}
                                aria-label={m.irrigation_schedule_active()}
                            />
                        )}
                    />
                </div>

                {canEdit ? (
                    <div className="space-y-2">
                        {values.active && otherActive && (
                            <p className="text-sm text-muted-foreground">
                                {m.irrigation_schedule_switch_hint({ other })}
                            </p>
                        )}
                        {!values.active && isActive && (
                            <p className="text-sm text-muted-foreground">
                                {m.irrigation_schedule_deactivate_hint()}
                            </p>
                        )}
                        <div className="flex justify-end">
                            {/* Chỉ disable khi đang gửi (chống bấm hai lần). */}
                            <Button type="submit" disabled={save.isPending}>
                                {save.isPending
                                    ? m.common_saving()
                                    : m.irrigation_schedule_save()}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        {m.irrigation_schedule_read_only()}
                    </p>
                )}
            </form>
        </FormProvider>
    )
}
